import React, { useState, useEffect } from 'react';
import {
    Box, Paper, Typography, Stack, CircularProgress,
    List, ListItem, ListItemText,
    Chip
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import axios from 'axios';
import { format } from 'date-fns';
import { getApiUrl } from '../../config/api';

const categoryColors: any = {
    ACADEMIC: { bg: '#EBF8FF', text: '#3182CE' },
    SPORTS: { bg: '#FFF5F5', text: '#E53E3E' },
    HOLIDAY: { bg: '#E6FFFA', text: '#38A169' },
    GENERAL: { bg: '#F7FAFC', text: '#4A5568' }
};

const EventsWidget = () => {
    const [events, setEvents] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchEvents = async () => {
            try {
                const userInfo = localStorage.getItem('userInfo');
                const token = userInfo ? JSON.parse(userInfo).token : null;
                const config = { headers: { Authorization: `Bearer ${token}` } };
                const res = await axios.get(getApiUrl('events'), config);

                const today = new Date();
                const upcoming = res.data
                    .filter((e: any) => new Date(e.startDate) >= today)
                    .sort((a: any, b: any) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
                    .slice(0, 5);

                setEvents(upcoming);
            } catch (error) {
                console.error('Fetch events error:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchEvents();
    }, []);

    if (loading) return (
        <Paper sx={{ p: 4, borderRadius: '24px', textAlign: 'center', bgcolor: 'white', border: '1px solid #edf2f7' }}>
            <CircularProgress size={24} sx={{ color: '#263B7C' }} />
        </Paper>
    );

    return (
        <Paper elevation={0} sx={{
            p: 3,
            borderRadius: '24px',
            border: '1px solid #edf2f7',
            bgcolor: 'white',
            display: 'flex',
            flexDirection: 'column'
        }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box sx={{
                        p: 1.2,
                        borderRadius: '12px',
                        bgcolor: '#F4F7FE',
                        color: '#263B7C',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <CalendarMonthIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" fontWeight={800} color="#263B7C" sx={{ lineHeight: 1.2 }}>
                            Calendar
                        </Typography>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            Upcoming school activities
                        </Typography>
                    </Box>
                </Stack>
                <Chip
                    label="View All"
                    size="small"
                    onClick={() => { }}
                    sx={{ fontWeight: 700, bgcolor: '#F4F7FE', color: '#263B7C', border: 'none', cursor: 'pointer' }}
                />
            </Stack>

            <List sx={{ p: 0 }}>
                {events.length === 0 ? (
                    <Box sx={{ py: 6, textAlign: 'center', opacity: 0.5 }}>
                        <EventIcon sx={{ fontSize: 48, mb: 1, color: '#CBD5E0' }} />
                        <Typography variant="body2" color="text.secondary">No upcoming events</Typography>
                    </Box>
                ) : (
                    events.map((event, index) => (
                        <ListItem key={event.id} sx={{ px: 0, py: 2, alignItems: 'center' }}>
                            <Box sx={{
                                minWidth: '55px',
                                textAlign: 'center',
                                mr: 2,
                                py: 1,
                                px: 0.5,
                                borderRadius: '14px',
                                bgcolor: '#F8FAFC',
                                border: '1px solid #edf2f7',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center'
                            }}>
                                <Typography variant="caption" color="text.secondary" fontWeight={800} sx={{ textTransform: 'uppercase', fontSize: '0.6rem' }}>
                                    {format(new Date(event.startDate), 'MMM')}
                                </Typography>
                                <Typography variant="h6" fontWeight={900} color="#263B7C" sx={{ lineHeight: 1 }}>
                                    {format(new Date(event.startDate), 'dd')}
                                </Typography>
                            </Box>
                            <ListItemText
                                primary={
                                    <Typography variant="subtitle2" fontWeight={800} color="#2D3748" noWrap>
                                        {event.title}
                                    </Typography>
                                }
                                secondary={
                                    <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                                        <Chip
                                            label={event.category}
                                            size="small"
                                            sx={{
                                                height: '18px',
                                                fontSize: '0.6rem',
                                                fontWeight: 900,
                                                textTransform: 'uppercase',
                                                bgcolor: categoryColors[event.category]?.bg || '#F7FAFC',
                                                color: categoryColors[event.category]?.text || '#4A5568',
                                                px: 0.5
                                            }}
                                        />
                                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                            {event.location || 'Campus'}
                                        </Typography>
                                    </Stack>
                                }
                            />
                        </ListItem>
                    ))
                )}
            </List>
        </Paper>
    );
};

export default EventsWidget;
